import React, {useState} from 'react';
import {Box, Button, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {map, Observable, of} from 'rxjs';

import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
import {clientsService} from '@store/clients';
import {clientsUnitsService} from '@store/clients/units';
import {suppliersUnitsService} from '@store/suppliers/units';
import {wasteFamiliesService} from '@store/settings/wasteFamilies';
import {containersService} from '@store/settings/containers';
import {serviceKindsService} from '@store/settings/serviceKinds';

import {Close} from '@icons/index';

import {Sizes} from '@constants/sizes.constants';
import AsyncMultiSelectWithSearchComponent
  from '@components/inputs/asyncMultiSelect/AsyncMultiSelectWithSearch.component';

interface TableReportFiltersDrawerProps {
  filters: TableFilter[];
  handleClose: () => void;
  handleSave: (tableFilters: TableFilter[]) => void;
}

const TableReportFiltersDrawer = (props: TableReportFiltersDrawerProps) => {
  const {filters, handleClose, handleSave} = props;

  const {t} = useTranslation();

  const [newFilters, setNewFilters] = useState<TableFilter[]>(filters);

  const updateFilters = (name: string, filterIndex: number) => (value: any) => {
    const filtersToUpdate = [...(newFilters || [])];
    filtersToUpdate[filterIndex] = {...filtersToUpdate[filterIndex], [name]: value};
    setNewFilters(filtersToUpdate);
  };

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack flex={1} overflow="hidden">
        <Stack mr="10px">
          <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb="12px" mr="-8px">
              <Typography fontSize={18} fontWeight="700">
                {t('global.filters')}
              </Typography>
              <IconButton onClick={handleClose}>
                <Close/>
              </IconButton>
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal"/>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack pt="20px" flex={1} spacing={1} overflow="hidden">
          <Stack spacing={3} overflow="auto" className="scrollable" py="20px" pr="10px">
            {newFilters.map((filter, index) => {
              if (filter.hide) return null;
              return (
                <Stack key={`Table filter ${filter.type} - ${index}`} mr="-5px">
                  <Stack pr="5px">
                    <AsyncMultiSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t(`global.filterFieldType.${filter.type}`)}
                      placeholder={t('global.selectOptions')}
                      readOnly={filter.disabled}
                      handleChange={updateFilters('values', index)}
                      values={filter.values ?? []}
                      getOptions={(search) => {
                        let searchObservable: Observable<any> = of([]);
                        switch (filter.type) {
                          case TableFilterType.SUPPLIER_ORGANISATION_UNIT:
                            const supplierIds = newFilters.find((of) => of.type === TableFilterType.SUPPLIER_ORGANISATION)?.values?.map((v) => v.value);
                            searchObservable = suppliersUnitsService.searchSuppliersUnits(search, supplierIds);
                            break;
                          case TableFilterType.CLIENT_ORGANISATION:
                            searchObservable = clientsService.searchClients(search);
                            break;
                          case TableFilterType.CLIENT_ORGANISATION_UNIT:
                            const clientIds = newFilters.find((of) => of.type === TableFilterType.CLIENT_ORGANISATION)?.values?.map((v) => v.value);
                            searchObservable = clientsUnitsService.searchClientsUnits(search, clientIds);
                            break;
                          case TableFilterType.SERVICE_KIND:
                            return serviceKindsService.searchServiceKinds(search).pipe(
                              map((options) => options.data.map((o) => ({label: o.kind || '-', value: o.id})))
                            );
                          case TableFilterType.WASTE:
                            const wasteFamilyIds = newFilters.find((of) => of.type === TableFilterType.WASTE_FAMILY)?.values?.map((v) => v.value);
                            searchObservable = wasteFamiliesService.searchWaste(search, wasteFamilyIds);
                            break;
                          case TableFilterType.WASTE_FAMILY:
                            searchObservable = wasteFamiliesService.searchWasteFamilies(search);
                            break;
                          case TableFilterType.CONTAINER:
                            searchObservable = containersService.searchContainers(search);
                            break;
                        }
                        return searchObservable?.pipe(
                          map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id})))
                        );
                      }}/>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
        <Box alignSelf="flex-end" justifySelf="flex-end" my="20px" mr="10px">
          <Button
            variant="contained"
            color="primary"
            disabled={JSON.stringify(filters) === JSON.stringify(newFilters)}
            onClick={() => handleSave(newFilters)}>
            {t('global.modify')}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default TableReportFiltersDrawer;
